import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid';

export type ODataOperator =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'ge'
  | 'lt'
  | 'le'
  | 'contains'
  | 'startswith'
  | 'endswith'
  | 'in';

export type ODataLogicalOperator = 'and' | 'or';

export type ODataValue = string | number | boolean | Date | null;

export type ODataFilter = {
  field: string;
  operator: ODataOperator;
  value: ODataValue;
  valueType?: 'string' | 'number' | 'boolean' | 'date' | 'datetime';
};

export type ODataFilterGroup = {
  filters: (ODataFilter | ODataFilterGroup)[];
  logicalOperator: ODataLogicalOperator;
};

export class ODataFilterBuilder {
  private filters: (ODataFilter | ODataFilterGroup)[] = [];
  private logicalOperator: ODataLogicalOperator = 'and';

  add = (
    field: string,
    operator: ODataOperator,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    this.filters.push({ field, operator, value, valueType });
    return this;
  };

  eq = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'eq', value, valueType);
  };

  ne = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'ne', value, valueType);
  };

  contains = (field: string, value: string): this => {
    return this.add(field, 'contains', value, 'string');
  };

  startsWith = (field: string, value: string): this => {
    return this.add(field, 'startswith', value, 'string');
  };

  endsWith = (field: string, value: string): this => {
    return this.add(field, 'endswith', value, 'string');
  };

  gt = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'gt', value, valueType);
  };

  ge = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'ge', value, valueType);
  };

  lt = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'lt', value, valueType);
  };

  le = (
    field: string,
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): this => {
    return this.add(field, 'le', value, valueType);
  };

  in = (field: string, values: ODataValue[]): this => {
    return this.add(field, 'in', values as any);
  };

  addGroup = (group: ODataFilterGroup): this => {
    this.filters.push(group);
    return this;
  };

  addRaw = (filterString: string): this => {
    if (filterString) {
      this.filters.push({
        field: '',
        operator: 'eq' as ODataOperator,
        value: filterString,
        valueType: 'raw' as any
      });
    }
    return this;
  };

  setLogicalOperator = (operator: ODataLogicalOperator): this => {
    this.logicalOperator = operator;
    return this;
  };

  build = (): string => {
    if (this.filters.length === 0) {
      return '';
    }

    const filterStrings = this.filters
      .map(filter => this.buildFilter(filter))
      .filter(Boolean);

    if (filterStrings.length === 0) {
      return '';
    }

    if (filterStrings.length === 1) {
      return filterStrings[0];
    }

    return filterStrings.join(` ${this.logicalOperator} `);
  };

  private buildFilter = (filter: ODataFilter | ODataFilterGroup): string => {
    if ('filters' in filter) {
      return this.buildFilterGroup(filter);
    }

    if ((filter as any).valueType === 'raw') {
      return filter.value as string;
    }

    if (filter.value === null || filter.value === undefined) {
      return `${filter.field} eq null`;
    }

    const formattedValue = this.formatValue(filter.value, filter.valueType);

    switch (filter.operator) {
      case 'contains':
        return `contains(${filter.field}, ${formattedValue})`;
      case 'startswith':
        return `startswith(${filter.field}, ${formattedValue})`;
      case 'endswith':
        return `endswith(${filter.field}, ${formattedValue})`;
      case 'in':
        if (Array.isArray(filter.value)) {
          const values = filter.value
            .map(v => this.formatValue(v, filter.valueType))
            .join(',');
          return `${filter.field} in (${values})`;
        }
        return '';
      default:
        return `${filter.field} ${filter.operator} ${formattedValue}`;
    }
  };

  private buildFilterGroup = (group: ODataFilterGroup): string => {
    const filterStrings = group.filters
      .map(filter => this.buildFilter(filter))
      .filter(Boolean);

    if (filterStrings.length === 0) {
      return '';
    }

    if (filterStrings.length === 1) {
      return filterStrings[0];
    }

    return `(${filterStrings.join(` ${group.logicalOperator} `)})`;
  };

  private formatValue = (
    value: ODataValue,
    valueType?: ODataFilter['valueType']
  ): string => {
    if (value === null) {
      return 'null';
    }

    const type = valueType || this.detectValueType(value);

    switch (type) {
      case 'string':
        return `'${String(value).replace(/'/g, "''")}'`;
      case 'number':
        return String(value);
      case 'boolean':
        return String(value).toLowerCase();
      case 'date':
        if (value instanceof Date) {
          return value.toISOString().split('T')[0];
        }
        return String(value);
      case 'datetime':
        if (value instanceof Date) {
          return value.toISOString();
        }
        return String(value);
      default:
        return `'${String(value).replace(/'/g, "''")}'`;
    }
  };

  private detectValueType = (value: ODataValue): ODataFilter['valueType'] => {
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date) return 'datetime';
    return 'string';
  };

  clear = (): this => {
    this.filters = [];
    return this;
  };

  static create = (): ODataFilterBuilder => {
    return new ODataFilterBuilder();
  };
}

export const convertGridFilterToOData = (
  filterModel: GridFilterModel,
  fieldMapping?: Record<string, string>
): string => {
  if (!filterModel.items || filterModel.items.length === 0) {
    return '';
  }

  const builder = ODataFilterBuilder.create();
  builder.setLogicalOperator(
    (filterModel.logicOperator || 'and') as ODataLogicalOperator
  );

  for (const item of filterModel.items) {
    if (
      !item.field ||
      item.value === undefined ||
      item.value === null ||
      item.value === ''
    ) {
      continue;
    }

    const field = fieldMapping?.[item.field] || item.field;
    addGridFilterItem(builder, field, item);
  }

  return builder.build();
};

const addGridFilterItem = (
  builder: ODataFilterBuilder,
  field: string,
  item: GridFilterItem
): void => {
  const value = item.value;

  switch (item.operator) {
    case 'equals':
    case '=':
      builder.eq(field, value);
      break;
    case '!=':
    case 'not':
      builder.ne(field, value);
      break;
    case '>':
    case 'after':
      builder.gt(field, value);
      break;
    case '>=':
    case 'onOrAfter':
      builder.ge(field, value);
      break;
    case '<':
    case 'before':
      builder.lt(field, value);
      break;
    case '<=':
    case 'onOrBefore':
      builder.le(field, value);
      break;
    case 'contains':
      builder.contains(field, String(value));
      break;
    case 'startsWith':
      builder.startsWith(field, String(value));
      break;
    case 'endsWith':
      builder.endsWith(field, String(value));
      break;
    case 'isEmpty':
      builder.eq(field, null);
      break;
    case 'isNotEmpty':
      builder.ne(field, null);
      break;
    case 'isAnyOf':
      if (Array.isArray(value)) {
        builder.in(field, value);
      }
      break;
    default:
      builder.eq(field, value);
  }
};

export const buildTextSearchFilter = (
  searchText: string,
  fields: string[]
): string => {
  if (!searchText || fields.length === 0) {
    return '';
  }

  const builder = ODataFilterBuilder.create();
  builder.setLogicalOperator('or');

  for (const field of fields) {
    builder.contains(field, searchText);
  }

  return builder.build();
};

export const combineFilters = (
  filters: string[],
  operator: ODataLogicalOperator = 'and'
): string => {
  const validFilters = filters.filter(Boolean);

  if (validFilters.length === 0) {
    return '';
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return validFilters.map(f => `(${f})`).join(` ${operator} `);
};
